// @flow

import React, {Component} from 'react';
import {shouldUpdate} from '@pb/kit-util-should-update';
import {withCssModule} from '@pb/kit-util-with-css-module';

import MoreLessButton from '../more-less-button';
import {Type, Separator, Display, SEPARATORS} from '../constants';
import styles from './more-less-array.component.module.styl';

import type {Node} from 'react';

import type {DisplayType, SeparatorType} from '../constants';

const classNames = withCssModule(styles);

/**
 * @component
 * @name MoreLessArray
 * @file
 Use a component to hide part of the data array.
 The component has the ability to display or hidden elements of the end part of the array.
 */

/**
 * MoreLessArray Props
 */
type Props = {|
    /**
     * Data items
     */
    data: Array<any>,
    /**
     * Number of items to be shown initially
     */
    count: number,
    /**
     * Counter of hidden items
     */
    hideElementCount?: number,
    /**
     * Separator between displaying data items
     */
    separator: SeparatorType,
    /**
     * Display style
     */
    displaying: DisplayType,
    /**
     * Set component to ellipsis style
     */
    ellipsis: boolean,
    /**
     * Call when click more/less button
     */
    onChange?: (value: boolean) => void,
    /**
     * Call after click more/less button
     */
    onAfterChange?: () => void,
    /**
     * Set component to show counter hidden items
     */
    counterShown: boolean,
    /**
     * Mixed class
     */
    mix: string,
    /**
     * Wrapper for data item
     */
    children: (item: any) => Node,
    /**
     * Text name of "more" button
     */
    moreText?: string,
    /**
     * Text name of "less" button
     */
    lessText?: string,
    /**
     * Render custom button
     */
    renderButton?: React$Node,
|};

type State = {|
    isCollapsed: boolean,
|};

export class MoreLessArray extends Component<Props, State> {
    static defaultProps = {
        count: 1,
        separator: Separator.NONE,
        displaying: Display.BLOCK,
        ellipsis: false,
        counterShown: true,
        mix: '',
        renderButton: null,
    };

    state = {
        isCollapsed: true
    };

    shouldComponentUpdate(nextProps: Props, nextState: State) {
        return nextProps.data.length > 0 && nextState.isCollapsed !== this.state.isCollapsed ||
            nextProps.data !== this.props.data;
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const {onAfterChange} = this.props;

        if (onAfterChange && shouldUpdate(this.state, prevState, ['isCollapsed'])) {
            onAfterChange();
        }
    }

    renderElement: (data: Array<HTMLDivElement>) => Array<any> = data => {
        const {children, separator, displaying} = this.props;
        const classWrapperElement = classNames('more-less-array__item', {
            'more-less-array__item_d-block': displaying === Display.BLOCK,
            'more-less-array__item_inline': displaying === Display.INLINE,
            'more-less-array__item_inline-block': displaying === Display.INLINE_BLOCK
        });
        return data.map((el, i) => (
            <div key={i} className={classWrapperElement}>
                {children(el)}{data.length - 1 > i ? SEPARATORS[separator] : ''}
            </div>
        ));
    };

    onToggle = () => {
        const {isCollapsed} = this.state;
        const {onChange} = this.props;

        if (onChange) {
            onChange(!isCollapsed);
        }

        this.setState({
            isCollapsed: !isCollapsed
        });
    };

    render() {
        const {data, displaying, ellipsis, hideElementCount, counterShown, mix, count, renderButton} = this.props;
        const dataLength = data.length || hideElementCount || 0;
        const {isCollapsed} = this.state;

        const displayClasses = classNames({
            'more-less-array_d-block': displaying === Display.BLOCK,
            'more-less-array_inline': displaying === Display.INLINE,
            'more-less-array_inline-block': displaying === Display.INLINE_BLOCK,
            [mix]: mix
        });

        if (dataLength <= count || count === 0 && !hideElementCount) {
            return (
                <div className={displayClasses}>
                    {this.renderElement(data)}
                </div>
            );
        }

        const counter = counterShown ? `+${dataLength - count} ` : '';
        const beforeMore = ellipsis ? `... ${counter}` : counter;
        const moreText = counterShown ? `${beforeMore}${this.props.moreText || 'more'}` : this.props.moreText;
        const lessText = counterShown ? this.props.lessText || 'less' : this.props.lessText;

        return (
            <div className={displayClasses}>
                {this.renderElement(isCollapsed ? data.slice(0, count) : data)}
                {renderButton
                    ? renderButton
                    : (
                        <MoreLessButton
                            {...{
                                isCollapsed,
                                moreText,
                                lessText,
                                type: Type.ARRAY,
                                onChange: this.onToggle
                            }}
                        />
                    )
                }
            </div>
        );
    }
}

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("../constants");
